import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Phone, PhoneCall, MessageSquare, Loader2, CheckCircle2, ArrowRightLeft,
  ShieldCheck, Repeat,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authFetch(path, opts = {}) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

const fmt = (e164) =>
  e164 && e164.startsWith('+1') && e164.length === 12
    ? `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`
    : e164;

// Canonical, customer-facing messaging status copy (shared by the number card
// and the compliance cards) — derived from real compliance/activation state,
// never from the number type. No raw provider jargon.
const MESSAGING_LABEL = {
  active: 'Active', activating: 'Activating', approved: 'Approved',
  under_review: 'Under review', submitted: 'Under review', action_needed: 'Action needed',
  ready_to_submit: 'Ready to submit', rejected: 'Needs attention', setup_needed: 'Setup needed',
  inactive: 'Inactive',
};

const msgStatusClass = (state) =>
  state === 'active' || state === 'approved' ? 'text-emerald-600 font-medium'
    : state === 'rejected' || state === 'action_needed' ? 'text-red-600 font-medium'
      : 'text-amber-600 font-medium';

const PORT_LABEL = {
  draft: 'Draft', portability_verified: 'Ready to transfer', information_needed: 'Information needed',
  ready_to_submit: 'Ready to submit', submitted: 'Submitted', in_progress: 'Transfer in progress',
  completed: 'Completed', failed: 'Failed', cancelled: 'Cancelled',
};

// ── Card shell ────────────────────────────────────────────────────────────────
function Card({ title, icon: Icon, children, testid }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5 lg:p-6 space-y-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-primary" />}
        <h2 className="text-lg font-semibold text-secondary">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', testid, optional }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-gray-700">
        {label}{optional && <span className="text-gray-400 text-xs font-normal"> (optional)</span>}
      </Label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        type={type} data-testid={testid} />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BusinessNumber() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [ports, setPorts] = useState([]);

  const refresh = async () => {
    const [s, p] = await Promise.all([
      authFetch('/api/telephony/business-number'),
      authFetch('/api/telephony/port-requests'),
    ]);
    if (s.ok) setStatus(s.data);
    if (p.ok) setPorts(p.data.portRequests || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm" data-testid="bn-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading business number…
      </div>
    );
  }

  const primary = status?.primary;
  const regime = primary?.messaging?.regime;

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-business-number">
      <div>
        <h1 className="text-2xl font-bold text-secondary">Business Number</h1>
        <p className="text-gray-500 text-sm mt-1">Manage the numbers customers use to call and text you.</p>
      </div>

      {/* Numbers — multi-number ready (today usually one primary) */}
      <Card title="Your numbers" icon={Phone} testid="bn-numbers">
        {primary ? (
          <div className="border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="bn-primary-number">
            <div>
              <div className="text-xl font-bold text-secondary">{primary.displayPhone || fmt(primary.phoneE164)}</div>
              <span className="inline-block mt-1 text-xs font-medium text-primary bg-primary/10 rounded-full px-2 py-0.5">Primary</span>
            </div>
            <div className="flex gap-6">
              <div className="text-sm">
                <div className="flex items-center gap-1.5 text-gray-500"><PhoneCall className="w-3.5 h-3.5" /> Calling</div>
                <div className={primary.calling.status === 'active' ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'} data-testid="bn-calling-status">
                  {primary.calling.status === 'active' ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="text-sm">
                <div className="flex items-center gap-1.5 text-gray-500"><MessageSquare className="w-3.5 h-3.5" /> Text messaging</div>
                <div className={msgStatusClass(primary.messaging.status)} data-testid="bn-messaging-status">
                  {MESSAGING_LABEL[primary.messaging.status] || 'Setup needed'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No business number yet. Finish onboarding or check portability of your existing number below.</p>
        )}
      </Card>

      {/* Transfer an existing number */}
      <PortTransferSection ports={ports} hasPrimary={!!primary} onChange={refresh} />

      {/* Messaging compliance — depends on regime */}
      {regime === 'toll_free' ? (
        <TollFreeSection phone={primary?.phoneE164} status={status?.messagingCompliance} onChange={refresh} />
      ) : (
        <TenDlcSection compliance={status?.messagingCompliance} onChange={refresh} />
      )}
    </div>
  );
}

// ── Port transfer + intake ────────────────────────────────────────────────────
function PortTransferSection({ ports, hasPrimary, onChange }) {
  const [phone, setPhone] = useState('');
  const [checking, setChecking] = useState(false);

  const check = async () => {
    if (!phone.trim()) return;
    setChecking(true);
    const { data } = await authFetch('/api/telephony/portability-check', {
      method: 'POST', body: JSON.stringify({ phone: phone.trim() }),
    });
    setChecking(false);
    if (!data.ok) { toast.error(data.message || "Couldn't check that number. Try again."); return; }
    toast.success(data.portable ? 'Good news — this number can be transferred.' : "This number can't currently be transferred to CleanBid.");
    setPhone('');
    onChange();
  };

  return (
    <Card title="Transfer an existing number" icon={ArrowRightLeft} testid="bn-transfer">
      <p className="text-gray-600 text-sm">
        {hasPrimary
          ? 'Already using CleanBid? Bring an additional or replacement business number in from another provider. First we check whether it can be transferred, then we collect the details from your current provider.'
          : 'Keep the number your customers already know. First we check whether it can be transferred, then we collect the details from your current provider.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 000-0000"
          data-testid="bn-check-input" className="flex-1" />
        <Button onClick={check} disabled={checking || !phone.trim()} data-testid="bn-check-btn">
          {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking…</> : <><Repeat className="w-4 h-4 mr-2" />Check number</>}
        </Button>
      </div>

      {ports.map((pr) => <PortRequestRow key={pr.id} pr={pr} onChange={onChange} />)}
    </Card>
  );
}

const XFER_LABEL = {
  not_started: 'Not started', preparing: 'Transfer being prepared',
  information_needed: 'Information needed', ready_to_submit: 'Ready to submit',
  submitted: 'Transfer submitted', carrier_review: 'Carrier review',
  action_needed: 'Action needed', scheduled: 'Transfer scheduled',
  voice_ported: 'Calling transferred', messaging_activating: 'Text messaging activating',
  completed: 'Transfer complete', issue: 'Transfer issue', cancelled: 'Cancelled',
};

async function authForm(path, formData) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  return { ok: res.ok, data: await res.json().catch(() => ({})) };
}

function PortRequestRow({ pr, onChange }) {
  const [busy, setBusy] = useState(false);
  const portable = pr.portabilityStatus === 'portable';
  const started = !!pr.hasStartedTransfer;
  const orders = pr.orders || [];
  const state = pr.transferState || 'not_started';

  const startTransfer = async () => {
    setBusy(true);
    const { data } = await authFetch(`/api/telephony/port-requests/${pr.id}/start-transfer`, { method: 'POST' });
    setBusy(false);
    if (data.ok) { toast.success('Transfer started — gathering requirements.'); onChange(); }
    else toast.error(data.message || 'Could not start the transfer.');
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid={`bn-port-${pr.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-secondary">{pr.displayPhone || fmt(pr.phoneE164)}</div>
          <div className="text-xs mt-0.5">
            {portable
              ? <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Can be transferred</span>
              : <span className="text-amber-600" data-testid={`bn-not-portable-${pr.id}`}>This number can’t currently be transferred to CleanBid.</span>}
            {started && <span className="ml-2 inline-block text-primary bg-primary/10 rounded-full px-2 py-0.5" data-testid={`bn-xfer-state-${pr.id}`}>{XFER_LABEL[state] || state}</span>}
          </div>
          {!portable && pr.notPortableReasonMessage && pr.notPortableReasonMessage !== "This number can’t currently be transferred to CleanBid." && (
            <div className="text-xs text-gray-500 mt-0.5" data-testid={`bn-not-portable-reason-${pr.id}`}>
              {pr.notPortableReasonMessage}
            </div>
          )}
        </div>
        {portable && !started && (
          <Button size="sm" onClick={startTransfer} disabled={busy} data-testid={`bn-start-transfer-${pr.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start number transfer'}
          </Button>
        )}
      </div>

      {started && orders.map((o) => (
        <OrderPanel key={o.orderRef} prId={pr.id} order={o} onChange={onChange} />
      ))}
    </div>
  );
}

function OrderPanel({ prId, order, onChange }) {
  const reqs = order.requirements || [];
  const [vals, setVals] = useState(() => Object.fromEntries(reqs.map((r) => [r.fieldId, r.fieldValue || ''])));
  const [busy, setBusy] = useState(false);
  const docs = order.documents || [];
  const hasLoa = docs.some((d) => d.type === 'loa');

  const saveReqs = async () => {
    setBusy(true);
    const values = reqs.map((r) => ({ fieldId: r.fieldId, fieldValue: vals[r.fieldId] || '' }));
    const { data } = await authFetch(`/api/telephony/port-requests/${prId}/requirements`, {
      method: 'POST', body: JSON.stringify({ orderRef: order.orderRef, values }),
    });
    setBusy(false);
    if (data.ok) { toast.success('Details saved.'); onChange(); } else toast.error('Could not save.');
  };

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('orderRef', order.orderRef); fd.append('docType', docType); fd.append('file', file);
    const { data } = await authForm(`/api/telephony/port-requests/${prId}/documents`, fd);
    if (data.ok) { toast.success(`${docType.toUpperCase()} uploaded.`); onChange(); }
    else toast.error(data.message || 'Upload failed.');
  };

  const submit = async () => {
    if (!window.confirm('Submit this number transfer to begin the carrier process? This starts the real transfer.')) return;
    setBusy(true);
    const { data } = await authFetch(`/api/telephony/port-requests/${prId}/submit`, {
      method: 'POST', body: JSON.stringify({ orderRef: order.orderRef }),
    });
    setBusy(false);
    if (data.ok) { toast.success('Transfer submitted.'); onChange(); }
    else toast.error(data.message || 'Could not submit.');
  };

  const ready = order.requirementsMet && hasLoa;
  const submitted = !!order.submitted;

  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-3" data-testid={`bn-order-${order.orderRef}`}>
      {order.lastComment && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid={`bn-order-comment-${order.orderRef}`}>
          Action needed: {order.lastComment}
        </div>
      )}
      {reqs.length > 0 && !submitted && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {reqs.map((r) => (
            <div key={r.fieldId} className="space-y-1">
              <Label className="text-sm text-gray-700">{r.name}{r.prefilledFromIntake && <span className="text-emerald-600 text-xs"> · from your info</span>}</Label>
              <Input value={vals[r.fieldId] || ''} onChange={(e) => setVals((p) => ({ ...p, [r.fieldId]: e.target.value }))}
                placeholder={r.description || ''} data-testid={`bn-req-${r.fieldId}`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Button size="sm" variant="outline" onClick={saveReqs} disabled={busy} data-testid={`bn-req-save-${order.orderRef}`}>Save details</Button>
          </div>
        </div>
      )}

      {!submitted && (
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="text-primary cursor-pointer" data-testid={`bn-upload-loa-${order.orderRef}`}>
            {hasLoa ? '✓ Authorization form uploaded — replace' : 'Upload authorization form (LOA)'}
            <input type="file" className="hidden" onChange={(e) => uploadDoc('loa', e.target.files[0])} />
          </label>
          <label className="text-primary cursor-pointer" data-testid={`bn-upload-invoice-${order.orderRef}`}>
            {docs.some((d) => d.type === 'invoice') ? '✓ Recent bill uploaded — replace' : 'Upload recent bill'}
            <input type="file" className="hidden" onChange={(e) => uploadDoc('invoice', e.target.files[0])} />
          </label>
        </div>
      )}

      {(order.focRequested || order.focActual) && (
        <div className="text-xs text-gray-500" data-testid={`bn-foc-${order.orderRef}`}>
          {order.focActual ? `Scheduled transfer: ${new Date(order.focActual).toLocaleDateString()}`
            : `Requested transfer: ${new Date(order.focRequested).toLocaleDateString()} (not yet confirmed by carrier)`}
        </div>
      )}

      {!submitted ? (
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-400">{ready ? 'Ready to transfer.' : 'Complete the details and upload the authorization form to continue.'}</p>
          <Button size="sm" onClick={submit} disabled={busy || !ready} data-testid={`bn-submit-${order.orderRef}`}>Submit number transfer</Button>
        </div>
      ) : (
        <div className="text-sm text-emerald-700 flex items-center gap-1 pt-2 border-t border-gray-200" data-testid={`bn-submitted-${order.orderRef}`}>
          <CheckCircle2 className="w-4 h-4" /> Transfer submitted — we'll keep this page updated.
        </div>
      )}
    </div>
  );
}

const COMPLIANCE_LABEL = {
  setup_needed: 'Setup needed', ready_to_submit: 'Ready to submit', submitted: 'Under review',
  under_review: 'Under review', action_needed: 'Action needed', approved: 'Approved',
  rejected: 'Needs attention', activating: 'Activating', active: 'Text messaging active',
};

function StatusPill({ state }) {
  const good = state === 'approved' || state === 'active';
  const bad = state === 'rejected' || state === 'action_needed';
  const cls = good ? 'text-emerald-700 bg-emerald-50' : bad ? 'text-red-700 bg-red-50' : 'text-amber-700 bg-amber-50';
  return <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${cls}`} data-testid="bn-compliance-state">{COMPLIANCE_LABEL[state] || 'Setup needed'}</span>;
}

function TollFreeSection({ phone, status, onChange }) {
  const [busy, setBusy] = useState(false);
  const [brn, setBrn] = useState({
    businessRegistrationNumber: '', businessRegistrationType: 'EIN', businessRegistrationCountry: 'US',
  });
  const state = status?.state;
  const done = state === 'approved' || state === 'active';

  useEffect(() => {
    authFetch('/api/telephony/tendlc-intake').then(({ ok, data }) => {
      if (!ok) return;
      const i = data.intake || {};
      setBrn((p) => ({
        businessRegistrationNumber: i.businessRegistrationNumber || p.businessRegistrationNumber,
        businessRegistrationType: i.businessRegistrationType || p.businessRegistrationType,
        businessRegistrationCountry: i.businessRegistrationCountry || p.businessRegistrationCountry,
      }));
    });
  }, []);

  const setB = (k) => (v) => setBrn((p) => ({ ...p, [k]: v }));
  const complete = brn.businessRegistrationNumber && brn.businessRegistrationType && brn.businessRegistrationCountry;

  const submit = async () => {
    if (!complete) { toast.error('Please complete your business registration details first.'); return; }
    setBusy(true);
    // Persist the registration details into the shared intake, then submit.
    await authFetch('/api/telephony/tendlc-intake', { method: 'PUT', body: JSON.stringify({ intake: brn }) });
    const { data } = await authFetch('/api/telephony/compliance/toll-free', {
      method: 'POST', body: JSON.stringify({ phone, extra: brn }),
    });
    setBusy(false);
    if (data.ok) { toast.success('Submitted for toll-free verification.'); onChange(); }
    else if (data.error === 'brn_incomplete') toast.error('Please complete all business registration fields.');
    else toast.error(data.message || 'Could not submit verification.');
  };

  return (
    <Card title="Text messaging (toll-free)" icon={ShieldCheck} testid="bn-tollfree">
      <p className="text-gray-600 text-sm">
        Toll-free numbers use a separate carrier verification (not the local-number process). Your business registration details are required for review.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Business registration number" value={brn.businessRegistrationNumber}
          onChange={setB('businessRegistrationNumber')} placeholder="e.g. EIN 12-3456789" testid="bn-tf-brn" />
        <div className="space-y-1.5">
          <Label className="text-sm text-gray-700">Registration type</Label>
          <select value={brn.businessRegistrationType} onChange={(e) => setB('businessRegistrationType')(e.target.value)}
            data-testid="bn-tf-brn-type"
            className="w-full rounded-md border border-gray-200 text-sm px-3 py-2 h-10 focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="EIN">EIN (US tax ID)</option>
            <option value="DUNS">DUNS</option>
            <option value="LEI">LEI</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm text-gray-700">Registration country</Label>
          <select value={brn.businessRegistrationCountry} onChange={(e) => setB('businessRegistrationCountry')(e.target.value)}
            data-testid="bn-tf-brn-country"
            className="w-full rounded-md border border-gray-200 text-sm px-3 py-2 h-10 focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        {state ? <StatusPill state={state} /> : <span className="text-xs text-gray-400">Not started</span>}
        <Button size="sm" onClick={submit} disabled={busy || done || !complete} data-testid="bn-tollfree-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit for verification'}
        </Button>
      </div>
    </Card>
  );
}

// ── Sole-proprietor identity verification (SMS code) ───────────────────────────
function SolePropOtp({ compliance, onChange }) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const otpState = compliance?.otpState;

  // Reconcile latest state on mount (resume after refresh / delivery failure).
  useEffect(() => {
    authFetch('/api/telephony/compliance/brand/otp/reconcile', { method: 'POST' })
      .then(({ data }) => { if (data?.identityVerified) onChange && onChange(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async () => {
    if (!pin || pin.length < 4) { toast.error('Enter the code we texted you.'); return; }
    setBusy(true);
    const { data } = await authFetch('/api/telephony/compliance/brand/otp/verify', {
      method: 'POST', body: JSON.stringify({ pin }),
    });
    setBusy(false);
    if (data.ok && data.identityVerified) { toast.success('Your business number is verified for texting.'); setPin(''); onChange && onChange(); }
    else if (data.ok) { toast.message('Code accepted — finishing verification…'); onChange && onChange(); }
    else toast.error(data.message || 'That code was incorrect or expired. Request a new one.');
  };

  const resend = async () => {
    setResending(true);
    const { data } = await authFetch('/api/telephony/compliance/brand/otp/trigger', { method: 'POST' });
    setResending(false);
    if (data.ok) { toast.success('We sent a new verification code by text.'); onChange && onChange(); }
    else toast.error(data.message || 'Could not send a code. Try again.');
  };

  const note = otpState === 'expired' ? 'Your last code expired — request a new one.'
    : otpState === 'failed' ? 'That code didn\'t match — request a new one and try again.'
    : 'We texted a verification code to your business phone. Enter it below.';

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3" data-testid="bn-soleprop-otp">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-amber-700" />
        <span className="text-sm font-medium text-amber-900">Verify your business number by text</span>
      </div>
      <p className="text-xs text-amber-800">{note}</p>
      <div className="flex items-center gap-2">
        <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 10))}
          inputMode="numeric" placeholder="Enter code" className="max-w-[160px]" data-testid="bn-otp-input" />
        <Button size="sm" onClick={verify} disabled={busy} data-testid="bn-otp-verify">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
        </Button>
        <Button size="sm" variant="outline" onClick={resend} disabled={resending} data-testid="bn-otp-resend">
          {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resend code'}
        </Button>
      </div>
    </div>
  );
}

// ── 10DLC Text Messaging Setup ─────────────────────────────────────────────────
function TenDlcSection({ compliance, onChange }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    authFetch('/api/telephony/tendlc-intake').then(({ ok, data }) => {
      setF({
        dbaName: '', legalName: '', entityType: '', ein: '', soleProprietor: false,
        industry: '', website: '', businessAddress: '', contactName: '', email: '', phone: '',
        street: '', city: '', state: '', postalCode: '',
        useCase: '', optInMethod: '', optInDescription: '', privacyPolicyUrl: '', termsUrl: '',
        sampleMessages: '', helpContact: '', disclosuresConfirmed: false,
        ...(ok ? (data.intake || {}) : {}),
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    const { ok } = await authFetch('/api/telephony/tendlc-intake', {
      method: 'PUT', body: JSON.stringify({ intake: f }),
    });
    setSaving(false);
    if (ok) { toast.success('Text messaging setup saved. We\'ll submit it for you when you\'re ready.'); onChange && onChange(); }
    else toast.error('Could not save. Try again.');
  };

  const [submitting, setSubmitting] = useState(false);
  const cState = compliance?.state;
  const brandApproved = compliance?.brand === 'approved';
  const submitForApproval = async () => {
    if (!window.confirm('Submit your business for text messaging approval? Mobile carriers will review your business and how you use messaging.')) return;
    setSubmitting(true);
    // Brand first; once approved, submit the campaign.
    const step = brandApproved ? 'campaign' : 'brand';
    const { data } = await authFetch(`/api/telephony/compliance/${step}`, { method: 'POST' });
    setSubmitting(false);
    if (data.ok && data.error === undefined) {
      toast.success(step === 'brand' ? 'Business submitted for review.' : 'Messaging campaign submitted.');
      onChange && onChange();
    } else if (data.missing) {
      toast.error(`Please complete: ${data.missing.join(', ')}`);
    } else {
      toast.error(data.message || 'Could not submit. Please try again.');
    }
  };

  if (!f) return <Card title="Text messaging setup" icon={MessageSquare}><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></Card>;

  return (
    <Card title="Text messaging setup" icon={MessageSquare} testid="bn-tendlc">
      <p className="text-gray-600 text-sm">
        Mobile carriers require businesses to verify who they are and how they use text messaging. CleanBid will submit this information for you — filling it in now means texting turns on faster.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Business name customers see (DBA)" value={f.dbaName} onChange={set('dbaName')} placeholder="Acme Roof Cleaning" testid="bn-dba" />
        <Field label="Legal company name" value={f.legalName} onChange={set('legalName')} placeholder="Acme Roof Cleaning LLC" testid="bn-legal" />
        <Field label="Business type" value={f.entityType} onChange={set('entityType')} placeholder="LLC, Corporation, Sole proprietor…" testid="bn-entity" />
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm text-gray-700 pt-6">
            <input type="checkbox" checked={!!f.soleProprietor} onChange={(e) => set('soleProprietor')(e.target.checked)} data-testid="bn-soleprop" />
            I'm a sole proprietor (no EIN)
          </label>
        </div>
        {!f.soleProprietor && (
          <Field label="EIN (tax ID)" value={f.ein} onChange={set('ein')} placeholder="12-3456789" testid="bn-ein" />
        )}
        <Field label="Industry" value={f.industry} onChange={set('industry')} placeholder="Home services" testid="bn-industry" />
        <Field label="Website" value={f.website} onChange={set('website')} placeholder="https://…" testid="bn-website" optional />
        <Field label="Business address" value={f.businessAddress} onChange={set('businessAddress')} placeholder="123 Main St, City, ST" testid="bn-bizaddr" />
        <Field label="Street" value={f.street} onChange={set('street')} placeholder="123 Main St" testid="bn-street" />
        <Field label="City" value={f.city} onChange={set('city')} placeholder="New Haven" testid="bn-city" />
        <Field label="State" value={f.state} onChange={set('state')} placeholder="CT" testid="bn-state" />
        <Field label="ZIP / postal code" value={f.postalCode} onChange={set('postalCode')} placeholder="06510" testid="bn-postal" />
        <Field label="Contact name" value={f.contactName} onChange={set('contactName')} placeholder="Owner / manager" testid="bn-contact" />
        <Field label="Contact email" value={f.email} onChange={set('email')} type="email" placeholder="name@business.com" testid="bn-email" />
        <Field label="Contact phone" value={f.phone} onChange={set('phone')} placeholder="(555) 000-0000" testid="bn-phone" />
      </div>

      <div className="space-y-3 pt-1">
        <Field label="How do you use text messaging?" value={f.useCase} onChange={set('useCase')} placeholder="e.g. appointment reminders and quote links" testid="bn-usecase" />
        <Field label="How do customers agree to receive texts? (opt-in)" value={f.optInMethod} onChange={set('optInMethod')} placeholder="e.g. they provide their number when requesting a quote" testid="bn-optin" />
        <div className="space-y-1.5">
          <Label className="text-sm text-gray-700">Describe your opt-in / message flow</Label>
          <textarea value={f.optInDescription} onChange={(e) => set('optInDescription')(e.target.value)}
            rows={3} data-testid="bn-optin-desc"
            className="w-full rounded-md border border-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Explain how and where customers consent to receive texts from you." />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm text-gray-700">Sample messages</Label>
          <textarea value={f.sampleMessages} onChange={(e) => set('sampleMessages')(e.target.value)}
            rows={2} data-testid="bn-samples"
            className="w-full rounded-md border border-gray-200 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Hi {name}, your quote from Acme is ready: {link}. Reply STOP to opt out." />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Privacy policy URL" value={f.privacyPolicyUrl} onChange={set('privacyPolicyUrl')} placeholder="https://…/privacy" testid="bn-privacy" optional />
          <Field label="Terms URL" value={f.termsUrl} onChange={set('termsUrl')} placeholder="https://…/terms" testid="bn-terms" optional />
        </div>
        <Field label="HELP reply contact" value={f.helpContact} onChange={set('helpContact')} placeholder="Phone or email for customers who reply HELP" testid="bn-help" />
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!f.disclosuresConfirmed} onChange={(e) => set('disclosuresConfirmed')(e.target.checked)} data-testid="bn-disclosures" className="mt-0.5" />
          I confirm customers are told message &amp; data rates may apply and how to opt out (reply STOP).
        </label>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-400">Saved as a draft. CleanBid submits it for you later — nothing is registered yet.</p>
        <Button onClick={save} disabled={saving} data-testid="bn-tendlc-save">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save setup'}
        </Button>
      </div>

      {/* Sole proprietors must verify identity via an SMS code before texting can be approved */}
      {compliance?.soleProp && compliance?.brandCreated && !compliance?.identityVerified && (
        <SolePropOtp compliance={compliance} onChange={onChange} />
      )}

      {/* Explicit review + submit for carrier approval */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-testid="bn-tendlc-submit-row">
        <div className="text-sm">
          <span className="text-gray-700 font-medium">Text messaging approval</span>{' '}
          {cState ? <StatusPill state={cState} /> : <span className="text-xs text-gray-400">Setup needed</span>}
        </div>
        {cState !== 'active' && (
          <Button size="sm" onClick={submitForApproval} disabled={submitting || cState === 'submitted' || cState === 'under_review'}
            data-testid="bn-tendlc-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" />
              : brandApproved ? 'Submit messaging campaign' : 'Submit for text messaging approval'}
          </Button>
        )}
      </div>
    </Card>
  );
}
